import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { Eye, EyeOff, Loader2, Check } from 'lucide-react';

const signUpSchema = z.object({
    full_name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
    email: z.string().email('Email inválido'),
    password: z
        .string()
        .min(8, 'Senha deve ter no mínimo 8 caracteres')
        .regex(/[a-z]/, 'Deve conter letra minúscula')
        .regex(/[A-Z]/, 'Deve conter letra maiúscula')
        .regex(/[0-9]/, 'Deve conter número'),
    role: z.enum(['personal', 'student']),
    phone: z.string().optional(),
    affiliate_code: z.string().optional(),
    terms: z.boolean().refine((val) => val === true, 'Você deve aceitar os termos'),
});

type SignUpForm = z.infer<typeof signUpSchema>;

export function SignUpPage() {
    const { signUp } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Get affiliate and invite code from URL if present
    const affiliateFromUrl = searchParams.get('ref') || searchParams.get('code');
    const inviteCodeFromUrl = searchParams.get('invite');
    const roleFromUrl = searchParams.get('role') as 'personal' | 'student' | null;

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting },
    } = useForm<SignUpForm>({
        resolver: zodResolver(signUpSchema),
        defaultValues: {
            role: roleFromUrl || 'personal',
            affiliate_code: affiliateFromUrl || '',
        },
    });

    const password = watch('password', '');
    const role = watch('role');

    const passwordChecks = {
        length: password.length >= 8,
        lowercase: /[a-z]/.test(password),
        uppercase: /[A-Z]/.test(password),
        number: /[0-9]/.test(password),
    };

    const onSubmit = async (data: SignUpForm) => {
        try {
            setError(null);
            await signUp(data.email, data.password, {
                full_name: data.full_name,
                role: data.role,
                phone: data.phone,
                affiliate_code: data.affiliate_code,
                invite_code: inviteCodeFromUrl || undefined,
            });
            setSuccess(true);
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message || 'Erro ao criar conta');
            } else {
                setError('Erro ao criar conta');
            }
        }
    };

    if (success) {
        return (
            <div className="animate-fade-in text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Eba! Conta criada com sucesso! 🎉
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Corre lá no seu email pra confirmar sua conta e começar a transformar vidas!
                </p>
                <Link to="/login" className="btn-primary">
                    Bora lá! 🚀
                </Link>
            </div>
        );
    }

    return (
        <div className="animate-fade-in">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Crie sua conta e comece a brilhar! ✨
                </h2>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                    Já faz parte da família?{' '}
                    <Link to="/login" className="link">
                        Entrar
                    </Link>
                </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {error && (
                    <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800">
                        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                    </div>
                )}

                {/* Role selection */}
                <div>
                    <label className="label">Você é</label>
                    <div className="grid grid-cols-2 gap-3">
                        <label
                            className={`flex items-center justify-center p-4 rounded-lg border-2 cursor-pointer transition-all ${role === 'personal'
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <input
                                {...register('role')}
                                type="radio"
                                value="personal"
                                className="sr-only"
                            />
                            <div className="text-center">
                                <span className="text-2xl mb-1 block">💪</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                    Personal Trainer
                                </span>
                            </div>
                        </label>
                        <label
                            className={`flex items-center justify-center p-4 rounded-lg border-2 cursor-pointer transition-all ${role === 'student'
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950'
                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <input
                                {...register('role')}
                                type="radio"
                                value="student"
                                className="sr-only"
                            />
                            <div className="text-center">
                                <span className="text-2xl mb-1 block">🏃</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                    Aluno
                                </span>
                            </div>
                        </label>
                    </div>
                </div>

                <div>
                    <label htmlFor="full_name" className="label">
                        Nome completo
                    </label>
                    <input
                        {...register('full_name')}
                        type="text"
                        id="full_name"
                        autoComplete="name"
                        className={`input ${errors.full_name ? 'input-error' : ''}`}
                        placeholder="Seu nome"
                    />
                    {errors.full_name && (
                        <p className="mt-1 text-sm text-red-500">{errors.full_name.message}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="email" className="label">
                        Email
                    </label>
                    <input
                        {...register('email')}
                        type="email"
                        id="email"
                        autoComplete="email"
                        className={`input ${errors.email ? 'input-error' : ''}`}
                        placeholder="seu@email.com"
                    />
                    {errors.email && (
                        <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="password" className="label">
                        Senha
                    </label>
                    <div className="relative">
                        <input
                            {...register('password')}
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="new-password"
                            className={`input pr-10 ${errors.password ? 'input-error' : ''}`}
                            placeholder="••••••••"
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? (
                                <EyeOff className="w-5 h-5" />
                            ) : (
                                <Eye className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                    {password && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                            {Object.entries(passwordChecks).map(([key, valid]) => (
                                <div
                                    key={key}
                                    className={`flex items-center gap-1.5 text-xs ${valid ? 'text-green-600' : 'text-gray-400'
                                        }`}
                                >
                                    <Check className={`w-3 h-3 ${valid ? '' : 'opacity-0'}`} />
                                    <span>
                                        {key === 'length' && '8+ caracteres'}
                                        {key === 'lowercase' && 'Minúscula'}
                                        {key === 'uppercase' && 'Maiúscula'}
                                        {key === 'number' && 'Número'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Affiliate code */}
                <div>
                    <label htmlFor="affiliate_code" className="label">
                        Código de indicação{' '}
                        <span className="text-gray-400 font-normal">(opcional)</span>
                    </label>
                    <input
                        {...register('affiliate_code')}
                        type="text"
                        id="affiliate_code"
                        className="input"
                        placeholder="Ex: JOAO10"
                    />
                    {affiliateFromUrl && (
                        <p className="mt-1 text-sm text-green-600">
                            ✓ Código de indicação aplicado automaticamente
                        </p>
                    )}
                </div>

                {/* Terms */}
                <div className="flex items-start gap-3">
                    <input
                        {...register('terms')}
                        type="checkbox"
                        id="terms"
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="terms" className="text-sm text-gray-600 dark:text-gray-400">
                        Eu concordo com os{' '}
                        <a href="#" className="link">
                            Termos de Uso
                        </a>{' '}
                        e{' '}
                        <a href="#" className="link">
                            Política de Privacidade
                        </a>
                    </label>
                </div>
                {errors.terms && (
                    <p className="text-sm text-red-500">{errors.terms.message}</p>
                )}

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full"
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Criando conta...
                        </>
                    ) : (
                        'Criar conta grátis'
                    )}
                </button>
            </form>
        </div>
    );
}
